import { useToday } from "@mapcraft-tasks/client-store/react";
import { useEffect } from "react";
import { usePhone } from "../../lib/mobile";
import { OutlineProvider, type useOutlineEngine } from "../../outline/controller";
import { Overlays } from "../overlays";
import { OutlineRow } from "./row";

/**
 * Аутлайн. Экран в системе один — «страница ноды»; Today, Inbox, проект и день это
 * не разные экраны, а та же страница с другим корнем (ui-spec Область 1).
 * Одна ветка рендера вместо пяти.
 */
export function Outline({ engine }: { engine: ReturnType<typeof useOutlineEngine> }) {
	const today = useToday();
	const { state, actions } = engine;

	// Клавиатура ловится на документе: в режиме навигации фокуса на строке нет,
	// и вешать обработчик на контейнер значило бы требовать клика по нему.
	useEffect(() => {
		const handler = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null;
			const typing = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA";
			if (typing && state.mode === "nav") {
				return;
			}
			actions.onKeyDown(event);
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [actions, state.mode]);

	return (
		<OutlineProvider value={engine}>
			{/* Запас под последней строкой даёт `.scroll-tail` — он снаружи, соседом списка
			    в скроллере страницы (node-page). Здесь его быть не может: доля экрана,
			    отмеренная внутри списка, растянула бы и пустую страницу. */}
			<div className="relative pt-2">
				{state.rows.length === 0 ? (
					<Empty />
				) : (
					state.rows.map((row) => <OutlineRow key={row.key} row={row} today={today} />)
				)}
			</div>
			<Overlays engine={engine} />
		</OutlineProvider>
	);
}

/** Пустой экран — приглашение, а не сообщение: он называет ровно то действие,
    которое здесь есть. Под пальцем это кнопка внизу, под клавиатурой — `Enter`. */
function Empty() {
	const phone = usePhone();
	return (
		<p className="py-6 text-sm font-light text-ink-muted">
			{phone ? (
				"Пусто. «Новая строка» внизу заведёт первую."
			) : (
				<>
					Пусто. <span className="kbd">Enter</span> — первая строка.
				</>
			)}
		</p>
	);
}
